import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { RatesOnDateView } from './RatesOnDateView';
import { useRatesOnDate } from './useRatesOnDate';

/**
 * The "rates on a date" lookup — a self-served widget.
 *
 * Owns its data hook and its `ErrorBoundary`. A too-early date is **not**
 * routed here: it is an expected answer the View renders in place, so the
 * boundary only ever catches genuine failures.
 */
export const RatesOnDate: React.FC = () => {
    const lookup = useRatesOnDate();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('settings.onDate.error.title'),
            errorMessage: t('settings.onDate.error.message'),
            errorRetry: t('settings.error.retry'),
        }),
        [t],
    );

    return (
        <ErrorBoundary
            data-testid="settings-on-date-error-boundary"
            fallbackMessage={copy.errorMessage}
            id="settings-on-date-error-boundary"
            onRetry={lookup.onRetry}
            resetKeys={[lookup.error]}
            retryLabel={copy.errorRetry}
            title={copy.errorTitle}
        >
            <RatesOnDateContent lookup={lookup} />
        </ErrorBoundary>
    );
};

type RatesOnDateContentProps = {
    lookup: ReturnType<typeof useRatesOnDate>;
};

const RatesOnDateContent: React.FC<RatesOnDateContentProps> = ({ lookup }) => {
    if (lookup.error) {
        throw lookup.error;
    }
    return <RatesOnDateView lookup={lookup} />;
};
